// src/pages/ProblemNumbers.jsx
// Logs → Problem numbers (3.60.0, the owner's design of 2026-10-14):
// receiver fax numbers that often fail. The Fax Portal judges each number on
// all clients' faxes to it over the last 30 days (numbers with 5 or more
// tries; busy lines alone never make a number "Not a fax line"):
//   Not a fax line  no delivery, and wrong number / no fax machine / call dropped
//   Hard to reach   under half delivered
// Everyone sees what they could see in Outbox (Super Admins every client,
// Admins their client, Managers their teams, Users their own faxes).
// Changing it: a client's Admin can mark a number for their own client
// (they pick the class); Super Admins can override the system's class for
// every client. Backend: GET /portal/logs/problem-numbers (routes/portal.js).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { useContactNames } from '../hooks/useContactNames';
import { forgetProblemClasses } from '../hooks/useProblemNumbers';
import { API_BASE } from '../firebase/config';
import { timeZone } from '../utils/timeZone';
import EmptyState from '../components/EmptyState';
import Loader from '../components/Loader';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import Emo from '../components/Emo';

const fmtNum = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };
const day = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric' }) : '—');
export const CLASS_LOOK = {
  not_fax: { icon: '⛔', color: 'var(--t-b71c1c)', bg: 'var(--c-fce4ec)' },
  congested: { icon: '⏳', color: 'var(--t-bf360c)', bg: 'var(--c-ffebee)' },   // 3.78.0
  hard: { icon: '⚠️', color: 'var(--t-e65100)', bg: 'var(--c-fff3e0)' },
  normal: { icon: '✓', color: 'var(--t-2e7d32)', bg: 'var(--c-e8f5e9)' },
};
export function ClassBadge({ cls, label, source }) {
  const l = CLASS_LOOK[cls] || CLASS_LOOK.normal;
  const by = source === 'support' ? 'set by Fax Portal support' : source === 'client' ? 'set by your Admin' : null;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', color: l.color, background: l.bg }}><Emo e={l.icon} /> {label}</span>
      {by && <span style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{by}</span>}
    </span>
  );
}

// 3.78.0 (owner): the top 3 senders, then "+ N more" opens the rest in place
// (the server sends the top 12 and how many there are in all)
function Senders({ yours }) {
  const [open, setOpen] = useState(false);
  const list = yours?.senders || [];
  const total = Math.max(yours?.sendersTotal || 0, list.length);
  const shown = open ? list : list.slice(0, 3);
  const more = total - shown.length;
  return (
    <>
      {shown.map(p => <div key={p.email} title={p.email}>{p.name || p.email} <span style={S.small}>({p.count})</span></div>)}
      {!open && more > 0 && <button type="button" onClick={() => setOpen(true)} style={{ ...S.ghost, padding: '2px 0', border: 'none', background: 'none', fontSize: 12, color: 'var(--t-00695c)' }}>+ {more} more</button>}
      {open && more > 0 && <div style={S.small}>and {more} more</div>}
      {open && list.length > 3 && <button type="button" onClick={() => setOpen(false)} style={{ ...S.ghost, padding: '2px 0', border: 'none', background: 'none', fontSize: 12, color: 'var(--t-00695c)' }}>Show fewer</button>}
    </>
  );
}

export default function ProblemNumbers() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [add, setAdd] = useState({ number: '', cls: 'hard', note: '' });

  const call = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API_BASE}/portal${path}`, { ...opts, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'That didn’t work.');
    return j;
  }, [getToken]);
  const load = useCallback(async () => {
    try { setData(await call('/logs/problem-numbers')); setError(''); } catch (e) { setError(e.message); }
  }, [call]);
  useEffect(() => { load(); }, [load]);
  const hoverFor = useContactNames(data?.data || [], ['number']);
  // 3.60.5 (owner): search, class filter, Show and a pager
  const [q, setQ] = useState('');
  const [cls, setCls] = useState('');
  const [show, setShow] = useState('50');
  const listed = (data?.data || []).filter(r => (!cls || r.cls === cls)
    && (!q || [r.number, r.label, r.system?.mainReasonText, ...(r.yours?.senders || []).flatMap(p => [p.name, p.email])].some(v => String(v || '').toLowerCase().includes(q.toLowerCase()) || String(v || '').replace(/\D/g, '').includes(q.replace(/\D/g, '') || '~'))));
  const { pageRows, pager } = usePager(listed, show);

  async function change(number, kind, cls, note = '') {
    setBusy(number + kind);
    try {
      const path = `/problem-numbers/${encodeURIComponent(number)}/${kind}`;
      if (cls) await call(path, { method: 'PUT', body: JSON.stringify({ cls, note }) });
      else await call(path, { method: 'DELETE' });
      forgetProblemClasses();
      notify({ tone: 'success', title: cls ? `${fmtNum(number)}: ${{ not_fax: 'Not a fax line', congested: 'High failure congestion', hard: 'Hard to reach', normal: 'Normal' }[cls]}` : `${fmtNum(number)}: back to the system’s check` });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save that", detail: e.message }); }
    setBusy('');
  }
  async function addNumber(e) {
    e.preventDefault();
    const d = add.number.replace(/\D/g, '').slice(-10);
    if (d.length !== 10) { notify({ tone: 'error', title: 'Enter a 10-digit US or Canada fax number' }); return; }
    await change(d, data.canOverride && !data.client ? 'override' : 'client', add.cls, add.note);
    setAdd({ number: '', cls: 'hard', note: '' });
  }
  async function removeMark(number, kind) {
    const ok = await confirm({ title: `Remove ${fmtNum(number)}?`, message: kind === 'override' ? 'The system’s own check applies again, for every client.' : 'The system’s own check applies again for your company.', confirmLabel: 'Remove' });
    if (ok) change(number, kind, null);
  }

  if (error && !data) return <div style={S.error}>{error}</div>;
  if (!data) return <Loader />;
  const sa = data.canOverride, all = sa && !data.client;
  const canAdd = data.canMark || all;

  return (
    <div>
      <p style={S.sub}>
        Fax numbers that often fail. Every day, and after each fax result, the Fax Portal checks each number’s last {data.rule.days} days on all clients’ faxes to it
        (numbers with {data.rule.minTries} or more tries). A busy line alone doesn’t make a number a problem.
        <br /><Emo e="⛔" /> <strong>Not a fax line</strong>: no fax got through, and it answered as a wrong number, a voice line or dropped the call.
        {' '}<Emo e="⏳" /> <strong>High failure congestion</strong>: busy most of the day, but it gets through at certain hours (worked out on the last 60 days). Its automatic retries wait for those hours.
        {' '}<Emo e="⚠️" /> <strong>Hard to reach</strong>: fewer than half the faxes got through.
        {' '}Once a number is High failure congestion or Hard to reach, it stays on the list until Fax Portal support or an Admin takes it off.
        {data.client && !all && <> You see your {sa ? 'picked client’s' : 'own'} faxes to these numbers.</>}
      </p>

      {canAdd && (
        <form onSubmit={addNumber} style={S.addRow}>
          <strong style={{ fontSize: 13, color: 'var(--t-37474f)' }}>{all ? 'Set a number for every client:' : `Mark a number for ${data.client?.name || 'your company'}:`}</strong>
          <input value={add.number} onChange={e => setAdd({ ...add, number: e.target.value })} placeholder="Fax number" style={{ ...S.input, width: 160 }} aria-label="Fax number" />
          <select value={add.cls} onChange={e => setAdd({ ...add, cls: e.target.value })} style={S.input} aria-label="Class">
            <option value="hard">Hard to reach</option>
            <option value="congested">High failure congestion</option>
            <option value="not_fax">Not a fax line</option>
            {all && <option value="normal">Normal (overrides the system)</option>}
          </select>
          <input value={add.note} onChange={e => setAdd({ ...add, note: e.target.value })} placeholder="Note (optional)" style={{ ...S.input, width: 200 }} aria-label="Note" />
          <button type="submit" disabled={!!busy} style={S.btn}>Save</button>
        </form>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Number, problem or sender" style={{ ...S.input, minWidth: 220 }} aria-label="Search problem numbers" />
        <select value={cls} onChange={e => setCls(e.target.value)} style={S.input} aria-label="Class">
          <option value="">Every class</option><option value="not_fax">Not a fax line</option><option value="congested">High failure congestion</option><option value="hard">Hard to reach</option><option value="normal">Normal (overridden)</option>
        </select>
        <ShowSelect value={show} onChange={setShow} style={S.input} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} labelStyle={{ fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600 }} />
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <th style={S.th}>Number</th><th style={S.th}>Class</th><th style={S.th}>Delivered ({data.rule.days} days, all {sa ? 'clients' : 'companies'})</th><th style={S.th}>Main problem</th>
            <th style={S.th}>Last delivered</th><th style={S.th}>{all ? 'Clients' : 'Your faxes'}</th><th style={S.th}>Sent by</th>{(canAdd || sa) && <th style={S.th}></th>}
          </tr></thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0' }}><EmptyState icon={<Emo e="✅" />} emptyText="No problem numbers. Every number you fax gets through often enough." /></td></tr>
            ) : pageRows.map((r, i) => {
              const sys = r.system;
              const myMark = r.marks?.find(m => m.accountId === data.client?.id);
              return (
                <tr key={r.number} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span title={hoverFor(data.client?.id || null, r.number) || undefined}>{fmtNum(r.number)}</span></td>
                  <td style={S.td}>
                    <ClassBadge cls={r.cls} label={r.label} source={r.source} />
                    {sa && r.override && sys && r.override.cls !== sys.cls && <div style={S.small}>The data says: {sys.label}</div>}
                    {/* 3.78.0 (owner): it never leaves the list by itself */}
                    {r.source === 'system' && (r.cls === 'hard' || r.cls === 'congested') && sys?.hardSince && <div style={S.small} title="It stays on the list until Fax Portal support or an Admin takes it off">
                      On the list since {day(sys.hardSince)}</div>}
                    {r.cls === 'congested' && <div style={S.small}>{sys?.goodHoursText ? <>Usually gets through: <strong>{sys.goodHoursText}</strong></> : 'Not enough faxes yet to tell when it gets through: retries follow office hours for now'}</div>}
                    {r.cls === 'normal' && sys?.backToNormalAt && <div style={S.small}>Back to normal on {day(sys.backToNormalAt)}</div>}
                    {all && r.marks?.length > 0 && <div style={S.small}>Marked by {r.marks.map(m => `${m.accountName} (${m.label})`).join(', ')}</div>}
                    {(r.override?.note || myMark?.note) && <div style={S.small}>“{myMark?.note || r.override.note}”</div>}
                  </td>
                  <td style={S.td}>{sys?.tries ? <><strong>{sys.rate}%</strong> <span style={S.small}>({sys.delivered} of {sys.tries})</span></> : <span style={S.small}>not enough faxes yet</span>}</td>
                  <td style={S.td}>{sys?.mainReasonText || '—'}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{sys?.lastDelivered ? day(sys.lastDelivered) : sys?.tries ? 'Not in 30 days' : '—'}</td>
                  <td style={S.td}>{all
                    ? (r.clients || []).map(c => <div key={c.accountId} style={{ fontSize: 12 }}>{c.accountName}: {c.delivered} of {c.tries}</div>)
                    : r.yours ? <>{r.yours.delivered} of {r.yours.tries} <span style={S.small}>delivered</span></> : '—'}</td>
                  <td style={{ ...S.td, fontSize: 12 }}><Senders yours={r.yours} /></td>
                  {(canAdd || sa) && (
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {sa && (
                        <select value="" disabled={!!busy} onChange={e => { const v = e.target.value; if (v === 'remove') removeMark(r.number, 'override'); else if (v) change(r.number, 'override', v); }} style={S.input} aria-label="Change for every client">
                          <option value="">For every client…</option>
                          <option value="not_fax">Not a fax line</option>
                          <option value="congested">High failure congestion</option>
                          <option value="hard">Hard to reach</option>
                          <option value="normal">Normal</option>
                          {r.override && <option value="remove">Use the system’s check</option>}
                        </select>
                      )}
                      {data.canMark && myMark && <button onClick={() => removeMark(r.number, 'client')} disabled={!!busy} style={S.ghost}>Remove our mark</button>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager {...pager} label="numbers" />
    </div>
  );
}

const S = {
  sub: { margin: '0 0 14px', color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.7, maxWidth: 1000 },
  small: { fontSize: 11.5, color: 'var(--t-78909c)' },
  addRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '10px 14px', marginBottom: 14 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  btn: { padding: '7px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  ghost: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
};
